import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { productService } from '../../../services/productService';

const TrendingAssemblies = ({ title, subtitle, cta_text, cta_link }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [trendingAssemblies, setTrendingAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadTrendingProducts();
  }, []);

  const loadTrendingProducts = async () => {
    try {
      setLoading(true);
      const products = await productService?.getTrending(6);
      
      // Map products to match the expected structure
      const mappedProducts = products?.map(product => ({
        id: product?.id,
        title: product?.name,
        creator: product?.manufacturerName || 'OpenGadget',
        creatorAvatar: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"><circle cx="12" cy="8" r="4"/><path d="M12 14c-6.1 0-8 4-8 4v2h16v-2s-1.9-4-8-4z"/></svg>',
        creatorAvatarAlt: `${product?.manufacturerName || 'OpenGadget'} logo`,
        image: product?.thumbnailPath || "/assets/images/trending-fallback.jpg",
        alt: product?.description || product?.name,
        components: 12,
        products: product?.soldCount || 0,
        likes: Math.floor(Math.random() * 3000) + 500,
        category: product?.categoryName || "Products",
        tags: [product?.categoryName || "Product", "3D Model", "Design"],
        views: product?.combinedViews || 0,
        isVerified: true
      })) || [];

      setTrendingAssemblies(mappedProducts);
      setError('');
    } catch (err) {
      console.error('Error loading trending products:', err);
      setError(err?.message || 'Failed to load trending products');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (e, productId) => {
    // Prevent navigation if clicking on action buttons
    const isActionButton = e?.target?.closest('button') || e?.target?.closest('a[href*="shopping-cart"]');
    if (isActionButton) {
      e?.stopPropagation();
      return;
    }
    navigate(`/3d-model-viewer?id=${productId}`);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="TrendingUp" size={16} />
              <span>Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-destructive">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="TrendingUp" size={16} />
            <span>Trending Now</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {title || 'Popular Assemblies'}
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {subtitle || `Discover the most popular model combinations created by our community.
            Get inspired and remix these assemblies for your own projects.`}
          </p>
        </div>

        {/* Assemblies Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {trendingAssemblies?.map((assembly) => (
            <div
              key={assembly?.id}
              className="group relative bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-floating transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              onMouseEnter={() => setHoveredCard(assembly?.id)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={(e) => handleCardClick(e, assembly?.id)}
            >
              {/* Image Container */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <Image
                  src={assembly?.image}
                  alt={assembly?.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay on Hover */}
                <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
                  hoveredCard === assembly?.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}>
                  <div className="absolute inset-0 flex items-center justify-center space-x-3">
                    <div 
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3 cursor-pointer"
                    >
                      <Icon name="Eye" size={14} className="mr-2" />
                      <span>Preview</span>
                    </div>
                  </div>
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium">
                  {assembly?.category}
                </div>

                {/* Views Count */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs flex items-center space-x-1">
                  <Icon name="Eye" size={12} />
                  <span>{(assembly?.views || 0).toLocaleString()} views</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                    {assembly?.title}
                  </h3>
                  <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <Icon name="Heart" size={16} />
                  </Button>
                </div>

                {/* Creator Info */}
                <div className="flex items-center space-x-3 mb-4">
                  <Image
                    src={assembly?.creatorAvatar}
                    alt={assembly?.creatorAvatarAlt}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-foreground">{assembly?.creator}</span>
                    {assembly?.isVerified && (
                      <Icon name="BadgeCheck" size={14} color="var(--color-primary)" />
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {assembly?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Icon name="ShoppingBag" size={14} />
                      <span>{assembly?.products?.toLocaleString()}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        // Handle like action
                      }}
                      className="flex items-center space-x-1 hover:text-destructive transition-colors"
                    >
                      <Icon name="Heart" size={14} />
                      <span>{assembly?.likes?.toLocaleString()}</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Package" size={14} />
                    <span>{assembly?.components} parts</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link to={cta_link || "/model-catalog"}>
            <Button variant="outline" size="lg" iconName="ArrowRight" iconPosition="right">
              {cta_text || 'View All Assemblies'}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TrendingAssemblies;